import { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';

// "A newer version is live - reload."
//
// A tab keeps running the code it loaded, for as long as it stays open;
// moving between pages inside the app never fetches new code. On
// 2026-10-15 a fix to the quote chat was deployed and a tab opened
// before it went on silently dropping what the fixed server sent - the
// server log said it worked, the screen said it did not, and nothing on
// the page said why. Every build now writes its id to /version.json; the
// page compares that with its own and says so when they differ.
//
// Checked on focus and every few minutes, never in development (the dev
// server has no version.json and reloads itself anyway).

const CHECK_EVERY_MS = 5 * 60_000;

async function liveBuildId(): Promise<string | null> {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const body = (await res.json()) as { buildId?: unknown };
    return typeof body.buildId === 'string' ? body.buildId : null;
  } catch {
    // Offline, or mid-deploy: say nothing rather than something wrong.
    return null;
  }
}

export function NewVersionBanner(): React.ReactElement | null {
  const [stale, setStale] = useState(false);

  useEffect(() => {
    if (!import.meta.env.PROD) return;
    let cancelled = false;
    const check = async (): Promise<void> => {
      const live = await liveBuildId();
      if (!cancelled && live !== null && live !== __BUILD_ID__) setStale(true);
    };
    void check();
    const timer = window.setInterval(() => void check(), CHECK_EVERY_MS);
    const onFocus = (): void => void check();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  if (!stale) return null;
  return (
    <div
      role="status"
      className="sticky top-0 z-50 flex flex-wrap items-center justify-center gap-3 border-b border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900"
    >
      <span>
        A newer version of the app is live. Save anything you are working on, then reload to use it.
      </span>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="inline-flex items-center gap-1.5 rounded-[var(--radius-button)] bg-amber-900 px-3 py-1 text-xs font-semibold text-white hover:brightness-110"
      >
        <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
        Reload
      </button>
    </div>
  );
}
